/**
 * Credit score display utilities.
 * Uses actual Xactus-pulled score when available, falls back to self-reported range.
 * When co-borrower exists, qualifying score is the LOWEST middle score.
 */

export const formatCreditScoreRange = (range) => {
  if (!range) return '—';
  const str = String(range).trim();
  return str.replace(/_/g, '-').replace(/\s*-\s*/g, '-').replace(/\s+/g, '-') || '—';
};

/**
 * Get the display credit score for a borrower (+ optional co-borrower).
 * If both have actual scores, returns the LOWEST (qualifying score for mortgage).
 * @param {object} borrower - Primary borrower data
 * @param {object} [coBorrower] - Co-borrower data (optional)
 * @returns {{ value, display, isActual, source, pulledAt, primaryScore, coScore }}
 */
export const getCreditScore = (borrower, coBorrower) => {
  // Case-insensitive source check — the column carries both 'xactus' (the
  // lowercase legacy write) and 'Xactus (simulated)' / 'Xactus' (the
  // current writer's casing). startsWith without lowering only matched
  // the legacy rows; borrowers pulled under the current writer were
  // silently downgraded to the self-reported range on the admin profile.
  const isXactusSource = (src) => !!src && String(src).toLowerCase().startsWith('xactus');
  const primaryActual = borrower?.fico_score_actual && isXactusSource(borrower?.fico_score_source)
    ? borrower.fico_score_actual
    : (borrower?.credit_score || null);
  const primaryHasActual = !!primaryActual;

  const coActual = coBorrower?.fico_score_actual && isXactusSource(coBorrower?.fico_score_source)
    ? coBorrower.fico_score_actual
    : (coBorrower?.credit_score || null);
  const coHasActual = !!coActual;

  // If either borrower has an actual pulled score, use actual scores
  if (primaryHasActual || coHasActual) {
    let qualifyingScore;
    if (primaryHasActual && coHasActual) {
      qualifyingScore = Math.min(primaryActual, coActual);
    } else {
      qualifyingScore = primaryActual || coActual;
    }

    const isCoBorrowerLower = coHasActual && primaryHasActual && coActual < primaryActual;

    return {
      value: qualifyingScore,
      display: String(qualifyingScore),
      isActual: true,
      source: isCoBorrowerLower ? 'Xactus (Co-Borrower — lower)' : 'Xactus',
      pulledAt: borrower?.fico_pulled_at || coBorrower?.fico_pulled_at || null,
      primaryScore: primaryActual,
      coScore: coActual,
    };
  }

  // Fall back to self-reported range
  const range = borrower?.credit_score_range || borrower?.fico_range;
  if (range) {
    return {
      value: null,
      display: formatCreditScoreRange(range),
      isActual: false,
      source: 'Application (self-reported)',
      pulledAt: null,
      primaryScore: null,
      coScore: null,
    };
  }

  return { value: null, display: '—', isActual: false, source: null, pulledAt: null, primaryScore: null, coScore: null };
};

/**
 * Color for a numeric FICO score.
 */
export const getCreditScoreColor = (score) => {
  if (!score) return '#9ca3af';
  if (score >= 740) return '#acf44e';
  if (score >= 700) return '#84cc16';
  if (score >= 660) return '#FBBF24';
  if (score >= 620) return '#f97316';
  return '#ff082f';
};
